import { useState } from "react";
import { Link } from "react-router";
import { isJoinMessage, splitJoinMessage } from "@atrium/shared";
import { chatTimestamp, clock } from "../../lib/format";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconArrowRight } from "../../components/icons";
import { UserContextMenu } from "./MessageMenus";
import type { ChatMessage, MessageActions } from "./types";

/**
 * System lines in a chat. Join logs get Discord's treatment: a green arrow in
 * the avatar gutter, the member's name inside the welcome line and a button to
 * wave at them. Anything else (calls, renames, pins) stays a plain grey line.
 */
export function SystemMessage({ msg, meId, actions, preview }: { msg: ChatMessage; meId: string; actions: MessageActions; preview?: boolean }) {
  if (!isJoinMessage(msg)) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 pl-[68px] text-sm text-fg-3">
        <span className="font-medium text-fg-2">{msg.author.displayName}</span> {msg.content}
        <span className="text-xs">{clock(msg.createdAt)}</span>
      </div>
    );
  }
  const [before, after] = splitJoinMessage(msg.content);
  const name = (
    <Link to={`/u/${msg.author.username}`} className="font-semibold text-fg hover:underline">
      {msg.author.displayName}
    </Link>
  );
  const canWave = !preview && !!actions.onWave && actions.canReply && msg.author.id !== meId;
  return (
    <div id={preview ? undefined : `m-${msg.id}`} className="group mt-2 flex gap-3 py-1 pl-4 pr-[8%] transition-colors md:pr-4 hover:bg-surface/50" data-join-message>
      <div className="flex w-9 shrink-0 justify-center pt-0.5">
        <IconArrowRight className="icon size-5 text-success" aria-label="Joined" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-base leading-6 text-fg-2">
          {before}
          {preview ? (
            name
          ) : (
            <UserContextMenu user={msg.author} meId={meId} canMention={actions.canReply} moderation={actions.moderation}>
              {name}
            </UserContextMenu>
          )}
          {after}
          <Tooltip label={new Date(msg.createdAt).toLocaleString("en-GB")}>
            <time dateTime={msg.createdAt} className="ml-2 text-xs text-fg-3 tabular">
              {chatTimestamp(msg.createdAt)}
            </time>
          </Tooltip>
        </p>
        {canWave && <WaveButton messageId={msg.id} onWave={() => actions.onWave!(msg)} />}
      </div>
    </div>
  );
}

/** Waves per join message, kept outside the component so scrolling it out of view doesn't reset the count. */
const MAX_WAVES = 2;
const waveCounts = new Map<string, number>();

function WaveButton({ messageId, onWave }: { messageId: string; onWave: () => Promise<void> | void }) {
  const [sending, setSending] = useState(false);
  const [waves, setWaves] = useState(() => waveCounts.get(messageId) ?? 0);
  return (
    <button
      type="button"
      disabled={sending || waves >= MAX_WAVES}
      onClick={async () => {
        if (waves >= MAX_WAVES) return;
        const next = waves + 1;
        waveCounts.set(messageId, next);
        setWaves(next);
        setSending(true);
        try {
          await onWave();
        } finally {
          setSending(false);
        }
      }}
      className="mt-1.5 inline-flex h-9 items-center gap-2 rounded-md border border-line bg-surface px-3 text-sm font-medium text-fg-2 transition-colors hover:border-line-strong hover:bg-surface-2 hover:text-fg disabled:opacity-60"
    >
      <span className="text-lg leading-none" aria-hidden>
        👋
      </span>
      Wave to say hi!
    </button>
  );
}
